/**
 * Aligns persisted table rows to the columns of the schema they are read with.
 *
 * Table rows are stored as positional arrays. Adding, removing or reordering a
 * column shifts every index of already stored rows, so a positional read can
 * return the value of a neighbouring column. Cells carry their own identity
 * (`hashCodeRef`, and for older data the column `title`), which is used here to
 * rebuild each row against the current column list.
 */

import { decodeHtmlEntities } from "./doorReportGenerator";

export interface IAlignedColumn {
    hashCode?: string;
    label?: string;
    normalizedLabel: string;
}

export interface IAlignmentReport {
    rowsShifted: number;
    rowsShiftedIndexes: number[];
    unmatchedCells: number;
}

export const stripTagsPlain = (value: any): string =>
    String(value === undefined || value === null ? "" : value).replace(/<[^>]*>/g, "");

export const normalizeTitle = (value: any): string =>
    decodeHtmlEntities(stripTagsPlain(value)).trim().toLowerCase();

export const cellHashCode = (cell: any): string => {
    if (!cell || typeof cell !== "object") {
        return "";
    }
    return cell.hashCodeRef || cell.field?.hashCode || "";
};

export const cellTitle = (cell: any): string => {
    if (!cell || typeof cell !== "object") {
        return "";
    }
    return normalizeTitle(cell.title !== undefined ? cell.title : cell.field?.label);
};

/**
 * Accepts schema subfields (hashCode/label) as well as rendered table columns
 * (hashCodeRef/title) and normalizes the column titles once per table.
 */
export function toAlignedColumns(columns: any[]): IAlignedColumn[] {
    return (Array.isArray(columns) ? columns : []).map((column) => {
        const label = column?.label !== undefined ? column.label : column?.title;
        return {
            hashCode: column?.hashCode || column?.hashCodeRef || column?.field?.hashCode,
            label,
            normalizedLabel: normalizeTitle(label),
        };
    });
}

export function parseTableRows(value: any): any[] | null {
    if (Array.isArray(value)) {
        return value;
    }
    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : null;
        } catch (e) {
            return null;
        }
    }
    return null;
}

/**
 * Rebuilds a single row so that cell N belongs to column N.
 * Columns without a matching cell become null, cells without a matching column
 * are dropped and counted in the report.
 */
export function alignRow(row: any[], columns: IAlignedColumn[], report?: IAlignmentReport): any[] {
    if (!Array.isArray(row)) {
        return columns.map(() => null);
    }

    const byHashCode = new Map<string, any>();
    const byTitle = new Map<string, any>();
    let identifiableCount = 0;

    row.forEach((cell) => {
        const hashCode = cellHashCode(cell);
        const title = cellTitle(cell);
        if (!hashCode && !title) {
            return;
        }
        identifiableCount += 1;
        if (hashCode && !byHashCode.has(hashCode)) {
            byHashCode.set(hashCode, cell);
        }
        if (title && !byTitle.has(title)) {
            byTitle.set(title, cell);
        }
    });

    if (identifiableCount === 0) {
        return row.length === columns.length ? row : columns.map(() => null);
    }

    const consumed = new Set<any>();
    const aligned = columns.map((column) => {
        if (column.hashCode) {
            const match = byHashCode.get(column.hashCode);
            if (match !== undefined) {
                consumed.add(match);
                return match;
            }
        }

        if (column.normalizedLabel) {
            const match = byTitle.get(column.normalizedLabel);
            if (match !== undefined && !consumed.has(match)) {
                consumed.add(match);
                return match;
            }
        }

        return null;
    });

    if (report) {
        report.unmatchedCells += identifiableCount - consumed.size;
    }

    return aligned;
}

export function emptyCellForColumn(column: IAlignedColumn): any {
    const cell: any = { title: column.label, value: undefined, field: null };
    if (column.hashCode) {
        cell.hashCodeRef = column.hashCode;
    }
    return cell;
}

export function hydrateAlignedRows(rows: any[][], columns: IAlignedColumn[]): any[][] {
    if (!Array.isArray(rows)) {
        return [];
    }

    return rows.map((row) =>
        Array.isArray(row)
            ? row.map((cell, index) =>
                  cell === null && columns[index] ? emptyCellForColumn(columns[index]) : cell
              )
            : row
    );
}

/**
 * Aligns every row of a table and reports which rows did not sit at the
 * positions the current schema expects.
 */
export function alignTableRows(
    rows: any[],
    columns: IAlignedColumn[]
): { rows: any[][]; report: IAlignmentReport } {
    const report: IAlignmentReport = {
        rowsShifted: 0,
        rowsShiftedIndexes: [],
        unmatchedCells: 0,
    };

    if (!Array.isArray(rows) || columns.length === 0) {
        return { rows: Array.isArray(rows) ? rows : [], report };
    }

    const alignedRows = rows.map((row, rowIndex) => {
        const aligned = alignRow(row, columns, report);
        const wasShifted =
            Array.isArray(row) &&
            (row.length !== aligned.length ||
                aligned.some((cell, index) => cell !== null && cell !== row[index]));

        if (wasShifted) {
            report.rowsShifted += 1;
            report.rowsShiftedIndexes.push(rowIndex + 1);
        }

        return aligned;
    });

    return { rows: alignedRows, report };
}
